import { Link } from "react-router-dom";
import { useTranslation } from "../i18n/LanguageContext";
import Logo from "./Logo";

function FooterLink({ to, children, external = false }) {
  const className = "text-slate-400 transition hover:text-white";
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function Footer() {
  const { language, setLanguage, t } = useTranslation();
  const linkLabels = {
    about: "About",
    contact: "Contact",
    careers: "Careers",
    events: "Events",
    news: "News & Reviews",
    buddies: "Buddies",
    cities: "Cities",
    artists: "Artists",
    organizers: "Organizers",
    venues: "Venues",
    privacyPolicy: "Privacy Policy",
    terms: "Terms",
    cookies: "Cookies",
  };
  const columns = [
    {
      title: "PartyBuddy",
      links: [
        { key: "about", to: "/about" },
        { key: "contact", to: "/contact" },
        { key: "careers", to: "/careers" },
      ],
    },
    {
      title: t("footer.discover", "Discover"),
      links: [
        { key: "events", to: "/" },
        { key: "news", to: "/events" },
        { key: "buddies", to: "/buddies" },
        { key: "cities", to: "/cities" },
      ],
    },
    {
      title: t("footer.creators", "Creators"),
      links: [
        { key: "artists", to: "/artists" },
        { key: "organizers", to: "/organizers" },
        { key: "venues", to: "/venues" },
      ],
    },
    {
      title: t("footer.legal", "Legal"),
      links: [
        { key: "privacyPolicy", to: "/privacy" },
        { key: "terms", to: "/terms" },
        { key: "cookies", to: "/cookies" },
      ],
    },
    {
      title: t("footer.social", "Social"),
      links: [
        {
          label: "Instagram",
          to: "https://instagram.com/partybuddy",
          external: true,
        },
        {
          label: "Facebook",
          to: "https://facebook.com/partybuddy",
          external: true,
        },
        {
          label: "TikTok",
          to: "https://tiktok.com/@partybuddy",
          external: true,
        },
      ],
    },
  ];

  return (
    <footer className="border-t border-slate-700/50 bg-blue-950/95 text-sm text-slate-300">
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
          {columns.map((column) => (
            <div key={column.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-200">
                {column.title}
              </h3>
              <ul className="space-y-1.5">
                {column.links.map((link) => (
                  <li key={link.label || link.key}>
                    <FooterLink to={link.to} external={link.external}>
                      {link.label || t(`footer.links.${link.key}`, linkLabels[link.key])}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-slate-700/50 pt-4 sm:flex-row">
          <div className="flex items-center gap-3">
            <Logo className="h-7 w-7" />
            <div className="text-center text-xs text-slate-400 sm:text-left">
              <p className="font-semibold text-slate-300">© PartyBuddy 2026</p>
              <p className="text-xs">{t("footer.tagline", "Made with ❤️ for people who love experiences")}</p>
            </div>
          </div>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-800/90 px-3 py-1.5 text-xs text-white shadow-inner"
            aria-label={t("common.language", "Language")}
          >
            <option value="de">GER</option>
            <option value="en">ENG</option>
          </select>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
